import type {
    AutomationRule,
    PaymentMatchValue,
    RuleMatch,
    TransmissionMatchValue,
} from '../worker-configuration/automation/worker-automation.types';

/**
 * Which worker rule covers a row of the Denials or Rejections table (FE-116, Jordan's v3 design).
 *
 * ## This is an ESTIMATE, and the page says so
 *
 * The worker does not tell us which rule a row would hit. This re-evaluates the account's rules
 * against the row in the browser, so it is only as right as our reading of the matching semantics.
 * Vadim decided (2026-10-02) to show it with that stated on screen until John confirms them.
 *
 * ## The semantics — the platform's, not the prototype's
 *
 * Jordan's prototype ANDs the CARC and RARC of a block and matches a RARC list on any one code. The
 * platform says otherwise, twice over (see `docs/denials-v3/README.md`):
 *
 * - our Workers page: "A RARC code is the alternative (OR). Use a second filter group if both must
 *   match (AND)";
 * - `enter-worker`'s rule classes: `carc.isMatch || rarc.isMatch`; CARC groups match on ANY listed
 *   group, CARC codes and RARC codes on ALL listed codes; blocks of one rule are ANDed.
 *
 * Those classes evaluate a whole PAYMENT, pooling codes from every line. A row here is one
 * `(group, code, remark set)` — one line's worth — so a rule needing two CARC codes that only meet
 * across lines cannot be seen to match. Such a row reads PARTIAL with the reason, never CONFIGURED:
 * the estimate errs toward "not covered", which sends a biller to look rather than away.
 *
 * ## Order
 *
 * Rules are taken lowest `priority` first and the first full match wins — the prototype's assumption
 * and the editor's own label ("the first match wins"). Unconfirmed; it is on John's list.
 */

export type CoverageStatus = 'configured' | 'partial' | 'none';

export interface ICoverageNearMiss {
    rule: AutomationRule;
    /** What the row lacks, in words: `RARC M127`. */
    missing: string;
}

export interface IWorkerCoverage {
    status: CoverageStatus;
    /** The rule that would act: the winner when configured, the conditional rule when partial by scope. */
    rule?: AutomationRule;
    /** Other rules that also match, in priority order — they lose to `rule`. */
    also: AutomationRule[];
    /** Why the status is partial, in words the table can show. */
    reason?: string;
    /** Rules that name the row's code or remark but do not match it. */
    near: ICoverageNearMiss[];
    /**
     * A conditional rule ordered ABOVE the winner. For the payers (or providers…) its condition names,
     * it fires first and the winner never runs — so the row is covered, but not by `rule` alone.
     */
    firstForSome?: { rule: AutomationRule; reason?: string };
}

/** One row of the denials table, in the terms a PAYMENT block speaks. */
export interface IDenialCoverageRow {
    /** The full enum name, as rules store it: `CONTRACTUAL_OBLIGATIONS`. */
    group: string;
    reasonCode: string;
    remarkCodes: string[];
}

const SCOPE_WORDS: Record<string, string> = {
    PAYER: 'payers',
    BILLING_PROVIDER: 'billing providers',
    RENDERING_PROVIDER: 'rendering providers',
    FACILITY: 'facilities',
    PROCEDURE_CODE: 'procedure codes',
    DATE_OF_SERVICE: 'dates of service',
};

const GROUP_SHORT: Record<string, string> = {
    CONTRACTUAL_OBLIGATIONS: 'CO',
    OTHER_ADJUSTMENTS: 'OA',
    PAYER_INITIATED_REDUCTIONS: 'PI',
    PATIENT_RESPONSIBILITY: 'PR',
    CORRECTIONS_AND_REVERSALS: 'CR',
};

const clean = (values: unknown): string[] =>
    Array.isArray(values) ? values.map(value => String(value).trim().toUpperCase()).filter(Boolean) : [];

const byPriority = (rules: AutomationRule[]): AutomationRule[] =>
    [...rules].sort(
        (a, b) => (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER),
    );

/** A scope this table has no dimension for: the rule can only ever be conditional here. */
const conditionWords = (blocks: RuleMatch[]): string => {
    const words = [...new Set(blocks.map(block => SCOPE_WORDS[block.scope] || block.scope.toLowerCase()))];
    const excluding = blocks.every(block => block.exclude);

    return `${excluding ? 'Except for some' : 'Only for some'} ${words.join(' and ')}`;
};

interface IBlockResult {
    matches: boolean;
    /** The block names the row's code or one of its remarks. */
    touches: boolean;
    missing: string;
}

const paymentBlock = (block: RuleMatch, row: IDenialCoverageRow): IBlockResult => {
    const value = (block.value || {}) as Partial<PaymentMatchValue>;
    const groups = clean(value.carc?.group);
    const codes = clean(value.carc?.codes);
    const rarcs = clean(value.rarc?.codes);
    const remarks = clean(row.remarkCodes);
    const code = row.reasonCode.trim().toUpperCase();

    // An empty list is not a condition. An empty RARC list beside a CARC must NOT read as "matches
    // anything" — validation never lets one be saved, and treating it as absent keeps it that way.
    const hasCarc = groups.length > 0 || codes.length > 0;
    const hasRarc = rarcs.length > 0;
    const carcOk =
        (groups.length === 0 || groups.includes(row.group.toUpperCase())) &&
        (codes.length === 0 || codes.every(c => c === code));
    const rarcOk = rarcs.every(r => remarks.includes(r));

    const matches = hasCarc && hasRarc ? carcOk || rarcOk : hasCarc ? carcOk : hasRarc ? rarcOk : false;
    const carcWords = [
        groups.length ? `group ${groups.map(g => GROUP_SHORT[g] || g).join('/')}` : '',
        codes.length ? `CARC ${codes.join(' + ')}` : '',
    ]
        .filter(Boolean)
        .join(' ');
    const rarcWords = hasRarc ? `RARC ${rarcs.join(' + ')}` : '';

    return {
        matches,
        touches: codes.includes(code) || rarcs.some(r => remarks.includes(r)),
        missing: [hasCarc ? carcWords : '', rarcWords].filter(Boolean).join(' or '),
    };
};

const transmissionBlock = (block: RuleMatch, message: string): IBlockResult => {
    const value = (block.value || {}) as Partial<TransmissionMatchValue> & { messageEquals?: unknown };
    const text = message.trim().toLowerCase();
    const phrases = (Array.isArray(value.contains) ? value.contains : [])
        .map(phrase => String(phrase).trim().toLowerCase())
        .filter(Boolean);
    const exact = (Array.isArray(value.messageEquals) ? value.messageEquals : [])
        .map(phrase => String(phrase).trim().toLowerCase())
        .filter(Boolean);
    const matches = phrases.some(phrase => text.includes(phrase)) || exact.includes(text);

    return { matches, touches: matches, missing: '' };
};

/**
 * One rule against one row: `full`, `conditional` (matches, but also has a condition this table
 * cannot see), `near` (names the row but misses it), or nothing.
 */
const evaluate = (
    rule: AutomationRule,
    ownScope: 'PAYMENT' | 'TRANSMISSION_STATUS',
    check: (block: RuleMatch) => IBlockResult,
): { kind: 'full' | 'conditional' | 'near' | 'none'; reason?: string; missing?: string } => {
    const blocks = rule.match || [];
    const own = blocks.filter(block => block.scope === ownScope && !block.exclude);
    const ownExcludes = blocks.filter(block => block.scope === ownScope && block.exclude);
    const other = blocks.filter(block => block.scope !== ownScope);

    if (!own.length) {
        // No condition on the codes at all: it fires on every row, subject to conditions we cannot see.
        return other.length ? { kind: 'conditional', reason: conditionWords(other) } : { kind: 'none' };
    }
    const results = own.map(check);
    if (ownExcludes.some(block => check(block).matches)) {
        return { kind: 'none' };
    }
    if (results.every(result => result.matches)) {
        return other.length ? { kind: 'conditional', reason: conditionWords(other) } : { kind: 'full' };
    }
    if (results.some(result => result.touches || result.matches)) {
        const missing = results.filter(result => !result.matches).map(result => result.missing).filter(Boolean);
        return { kind: 'near', missing: missing.join(' and ') };
    }

    return { kind: 'none' };
};

const coverage = (
    rules: AutomationRule[],
    ownScope: 'PAYMENT' | 'TRANSMISSION_STATUS',
    check: (block: RuleMatch) => IBlockResult,
): IWorkerCoverage => {
    const full: AutomationRule[] = [];
    const conditional: Array<{ rule: AutomationRule; reason?: string }> = [];
    const near: ICoverageNearMiss[] = [];

    byPriority(rules).forEach(rule => {
        const result = evaluate(rule, ownScope, check);
        if (result.kind === 'full') {
            full.push(rule);
        } else if (result.kind === 'conditional') {
            conditional.push({ rule, reason: result.reason });
        } else if (result.kind === 'near') {
            near.push({ rule, missing: result.missing || 'a condition' });
        }
    });

    if (full.length) {
        const winner = full[0];
        const also = byPriority([...full.slice(1), ...conditional.map(entry => entry.rule)]);
        const order = byPriority(rules);
        const before = conditional.find(entry => order.indexOf(entry.rule) < order.indexOf(winner));
        return {
            status: 'configured',
            rule: winner,
            also,
            near: [],
            ...(before ? { firstForSome: { rule: before.rule, reason: before.reason } } : {}),
        };
    }
    if (conditional.length) {
        return {
            status: 'partial',
            rule: conditional[0].rule,
            also: conditional.slice(1).map(entry => entry.rule),
            reason: conditional[0].reason,
            near,
        };
    }
    if (near.length) {
        return {
            status: 'partial',
            also: [],
            reason: `“${near[0].rule.name}” also needs ${near[0].missing}`,
            near,
        };
    }

    return { status: 'none', also: [], near: [] };
};

export const denialCoverage = (row: IDenialCoverageRow, rules: AutomationRule[]): IWorkerCoverage =>
    coverage(rules, 'PAYMENT', block => paymentBlock(block, row));

/** Only meaningful for REJECTED statuses — the Rejection Worker evaluates nothing else. */
export const rejectionCoverage = (message: string, rules: AutomationRule[]): IWorkerCoverage =>
    coverage(rules, 'TRANSMISSION_STATUS', block => transmissionBlock(block, message));

/** The first phrase of a rule that the message contains — what the table quotes as "Matches …". */
export const matchedPhrase = (message: string, rule: AutomationRule): string | undefined => {
    const text = message.toLowerCase();
    for (const block of rule.match || []) {
        if (block.scope !== 'TRANSMISSION_STATUS' || block.exclude) {
            continue;
        }
        const phrases = ((block.value as Partial<TransmissionMatchValue>).contains || []).map(p => String(p).trim());
        const hit = phrases.find(phrase => phrase && text.includes(phrase.toLowerCase()));
        if (hit) {
            return hit;
        }
    }

    return undefined;
};

/**
 * A rule's place in the order the worker reads them, 1-based — what "Priority N" means on screen.
 *
 * Its position, not its `priority` field: saved rules can lack one (the parser keeps their stored
 * order then), and the Workers page renumbers 1…n on every save anyway.
 */
export const rulePosition = (rules: AutomationRule[], rule: AutomationRule): number =>
    byPriority(rules).findIndex(candidate => candidate.id === rule.id) + 1;

/** The line under the badge: which rule wins and why, what is missing, or that nothing matches. */
export const coverageNote = (coverage: IWorkerCoverage, rules: AutomationRule[], noneText: string): string => {
    if (coverage.status === 'configured' && coverage.rule) {
        const position = rulePosition(rules, coverage.rule);
        if (coverage.firstForSome) {
            // "Only for some payers" -> "for some payers": where the earlier rule's condition holds, IT acts.
            const where = (coverage.firstForSome.reason || 'in some cases').replace(/^Only /, '').replace(/^E/, 'e');
            return `Priority ${position} \u2014 \u201C${coverage.firstForSome.rule.name}\u201D runs first ${where}`;
        }
        return coverage.also.length
            ? `Also matched by “${coverage.also[0].name}” — priority ${position} wins`
            : `Priority ${position}`;
    }
    if (coverage.status === 'partial') {
        return coverage.reason || 'Partly covered';
    }

    return noneText;
};

export type CoverageFilter = 'all' | CoverageStatus;

export const coverageCounts = (statuses: CoverageStatus[]): Record<CoverageFilter, number> => ({
    all: statuses.length,
    none: statuses.filter(status => status === 'none').length,
    partial: statuses.filter(status => status === 'partial').length,
    configured: statuses.filter(status => status === 'configured').length,
});
